import React, { useEffect } from "react";
import { Box } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import MainMetricsBar from "./MainMetricsBar";
import SystemsSidebar from "./SystemsSidebar";
import SystemDetailPanel from "./SystemDetailPanel";
import ScrollableWrapper from "../../../platform/components/common/ScrollableWrapper";
import {
  platformActions,
  RootStatePlatform,
} from "../../../platform/store/platform-slice";
import BodyMapTab from "../../../platform/components/score/BodyMapTab";
import BodyCompositionView from "./BodyCompositionView";
import { useConsultation } from "../../ConsultationContext";
import {
  ENERGY_TRAINING_SUB_ID,
  findSystemBySubId,
} from "./systemMapping";
import { MetricType } from "../../../platform/store/helper";

const BODY_COMPOSITION_SUB_ID = "body-composition";

/**
 * Section 2 — "Deep dive" layout from Figma node 14711:148285.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │                    MainMetricsBar                        │  ← always visible (no scroll)
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Sidebar      │ Detail panel             │ Body map       │  ← three independent columns
 *   │ (scroll)     │ (scroll)                 │ (visual only)  │
 *   └──────────────────────────────────────────────────────────┘
 *
 * The body map column reuses the score screen's `BodyMapTab` for visual
 * parity (same organs, same connection lines). It's wrapped in a
 * `pointer-events: none` container so the consultation doesn't trigger
 * any of its click side-effects — clicks remain functional in the score
 * screen where the same component is mounted normally.
 *
 * The body map renders for every system except Energy & Training (which
 * has no organ mapping and gets a 2-column layout instead).
 *
 * The `selectedSystem` Redux state drives which organs/lines light up.
 * We dispatch it from this component so the body map mirrors the active
 * sidebar selection, and we reset to 0 on unmount so the score screen
 * isn't left with stale selection state.
 */
const SystemsSection: React.FC = () => {
  const dispatch = useDispatch();
  const theme: Theme = useTheme();
  const { stepper, data } = useConsultation();
  const subId = stepper.current.sub.id;
  const isBodyComposition = subId === BODY_COMPOSITION_SUB_ID;
  const isEnergyTraining = subId === ENERGY_TRAINING_SUB_ID;
  const selectedSystem = useSelector(
    (state: RootStatePlatform) => state.platform.selectedSystem
  );
  // The DOM id of the currently-selected main system dot (e.g. "HEART",
  // "LUNG"). Used by the selective click-capture below to block clicks on
  // just that dot — connection dots remain clickable so users can pull up
  // the description text, same as the score screen.
  const mainSystemDomId =
    selectedSystem > 0 ? MetricType[selectedSystem] : null;
  // Right-column rendering:
  //   - Energy & Training → no right column (2-column layout)
  //   - Body Composition → BodyCompositionView (dedicated figure + tabs)
  //   - Anything else    → BodyMapTab (the score-screen organ figure)
  const showBodyMap = !isEnergyTraining && !isBodyComposition;

  // Mirror the active system into Redux so BodyMapTab renders the right
  // organ links + dots. When BodyMapTab isn't shown we clear the selection
  // so the score screen isn't left with a stray id.
  useEffect(() => {
    if (!showBodyMap) {
      dispatch(platformActions.setSelectedSystem(0));
      dispatch(platformActions.setSelectedActiveConnection(0));
      return;
    }
    const system = findSystemBySubId(data.systems ?? [], subId);
    if (system) {
      dispatch(platformActions.setSelectedSystem(system.system));
      dispatch(platformActions.setSelectedActiveConnection(0));
    }
  }, [subId, showBodyMap, dispatch, data.systems]);

  // Cleanup on unmount — leave the score screen with no leftover selection
  // when the user navigates away from Section 2.
  useEffect(() => {
    return () => {
      dispatch(platformActions.setSelectedSystem(0));
      dispatch(platformActions.setSelectedActiveConnection(0));
    };
  }, [dispatch]);

  return (
    <Box component="section" sx={styles.wrap}>
      <MainMetricsBar />
      <Box
        sx={isEnergyTraining ? styles.twoColumn : styles.threeColumn}
      >
        <Box sx={styles.column}>
          <ScrollableWrapper customPadding="0">
            <SystemsSidebar />
          </ScrollableWrapper>
        </Box>
        <Box sx={styles.column}>
          <ScrollableWrapper customPadding="0">
            <SystemDetailPanel />
          </ScrollableWrapper>
        </Box>

        {showBodyMap && (
          // BodyMapTab from the score screen. We capture clicks at the
          // column level (instead of using `pointer-events: none`, which
          // would also kill scroll-wheel events) so we can SELECTIVELY
          // suppress clicks on the currently-selected system's main dot —
          // otherwise clicking it would toggle the system off via Redux,
          // out-of-sync with the stepper-driven selection. Clicks on
          // connection dots and everything else pass through so users can
          // see the connection description (same UX as the score screen).
          <Box
            sx={styles.bodyMapColumn}
            onClickCapture={(e) => {
              if (!mainSystemDomId) return;
              const idHost = (e.target as HTMLElement).closest("[id]");
              if (idHost && (idHost as HTMLElement).id === mainSystemDomId) {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            <Box sx={styles.bodyMapContainer(theme)}>
              <BodyMapTab />
            </Box>
          </Box>
        )}

        {isBodyComposition && (
          // Body Composition uses a dedicated figure (not BodyMapTab) and
          // needs the tab picker to be clickable, so we don't capture
          // clicks here.
          <Box sx={styles.bodyMapColumn}>
            <Box sx={styles.bodyMapContainer(theme)}>
              <BodyCompositionView />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SystemsSection;

const styles = {
  wrap: {
    width: "100%",
    maxWidth: "1408px",
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    gap: "16px",
  },
  twoColumn: {
    display: "grid",
    gridTemplateColumns: "270px 1fr",
    gap: "16px",
    flex: 1,
    minHeight: 0,
    alignItems: "stretch",
  },
  threeColumn: {
    display: "grid",
    gridTemplateColumns: "270px 1fr 540px",
    gap: "16px",
    flex: 1,
    minHeight: 0,
    alignItems: "stretch",
  },
  column: {
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
  },
  bodyMapColumn: {
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
    // Click suppression is handled by `onClickCapture` on this Box (see
    // JSX above) so scroll wheel events still reach the inner
    // ScrollableWrapper. Clicks still work in the score screen.
  },
  // Figma container styling for the body-map panel (node 14711:148001):
  // translucent white, soft 1px border, 16px radius, large drop shadow.
  // No bottom padding (figure sits flush) and no right padding either —
  // ScrollableWrapper sits flush against the container's right edge so the
  // scrollbar appears on the edge instead of leaving a 16px gap inside.
  // (We can't drop ScrollableWrapper's own 10px content paddingRight because
  // it's used everywhere; this just removes the gap between its scrollbar
  // and our outer container's border.)
  bodyMapContainer: (theme: Theme) => ({
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
    backgroundColor: `${theme.colors_ds.NEUTRAL_BG_MAIN}59`,
    border: "1px solid rgba(0,0,0,0.05)",
    borderRadius: "16px",
    boxShadow: "0px 25px 50px 0px rgba(0,0,0,0.05)",
    padding: 0,
    overflow: "hidden",
  }),
};
